import { useState } from 'react';
import { Loader2, Phone } from 'lucide-react';
import { leadsApi } from '@/api/leads';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { toast } from 'sonner';

function digitsForTel(phone: string): string {
  const d = phone.replace(/\D/g, '');
  if (d.length >= 10) return d.length === 10 ? `+91${d}` : d.startsWith('91') ? `+${d}` : d;
  return phone.trim();
}

function isMobileBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

type Props = {
  leadId: string;
  phone: string;
  patientName?: string;
  className?: string;
};

/**
 * Desktop: queues call on the logged-in user's Sales CRM mobile app (push + poll).
 * Mobile browser: deep-link into Sales app, then tel: fallback.
 */
export function LeadQuickCallButton({ leadId, phone, patientName, className }: Props) {
  const [loading, setLoading] = useState(false);
  const tel = digitsForTel(phone);

  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!tel) return;

    if (isMobileBrowser()) {
      const appUrl = `thmssales://call?leadId=${encodeURIComponent(leadId)}`;
      window.location.href = appUrl;
      window.setTimeout(() => {
        window.location.href = `tel:${tel}`;
      }, 700);
      return;
    }

    setLoading(true);
    try {
      const data = await leadsApi.requestRemoteCall(leadId);
      toast.success(`Calling ${data.patientName || patientName || 'lead'}`, {
        description:
          'Open Sales CRM on your phone — the dialer opens there. Enable dialer call recording + link folder in Profile (Android) for clear two-sided uploads; laptop never records.',
        duration: 8000,
      });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not send call to your mobile app'), {
        description:
          'Log in to the Sales CRM app on your phone (same account) and allow notifications.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      title="Call on your phone (Sales CRM app). Recording uses the phone mic, not this computer."
      aria-label="Call on your phone"
      disabled={loading}
      onClick={(e) => void onClick(e)}
      className={cn(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm transition hover:bg-emerald-100 hover:text-emerald-900 disabled:opacity-60',
        className,
      )}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
    </button>
  );
}
